import type { ParsedLog, CategoryListItem, AccountListItem } from '../api-client';

export interface ReviewCardCallbacks {
  onAccept(edited: ParsedLog, fromAccountId: string): void;
  onCancel(): void;
  onEditCategory(current: ParsedLog): void;
  onEditAccount(currentAccountId: string): void;
}

export function renderReviewCard(
  root: HTMLElement,
  parsed: ParsedLog & { source?: string },
  cb: ReviewCardCallbacks,
  categories: CategoryListItem[],
  accounts: AccountListItem[],
  selectedAccountId: string,
) {
  const needsReview = parsed.confidence < 0.8;
  const catLabel = formatCategoryLabel(parsed.categoryId, parsed.subcategoryId, categories);
  const account = accounts.find((a) => a.id === selectedAccountId);
  const accountLabel = account?.name ?? selectedAccountId;

  root.innerHTML = `
    <div class="review-card">
      <div class="review-row">
        <span class="review-label">Amount</span>
        <span class="review-value">${parsed.amountOriginal} ${parsed.currencyOriginal}</span>
      </div>
      <div class="review-row">
        <span class="review-label">Merchant</span>
        <span class="review-value">${escapeHtml(parsed.merchant ?? '—')}</span>
      </div>
      <div class="review-row tappable" id="review-cat">
        <span class="review-label">Category</span>
        <span class="review-value">${escapeHtml(catLabel)} ›</span>
      </div>
      <div class="review-row">
        <span class="review-label">Type</span>
        <span class="review-value">${parsed.type}</span>
      </div>
      <div class="review-row tappable" id="review-account">
        <span class="review-label">on</span>
        <span class="review-value">${escapeHtml(accountLabel)} ▼</span>
      </div>
      ${needsReview ? '<p class="muted review-needs">AI wasn\'t sure — check before accepting.</p>' : ''}
      <div class="review-actions">
        <button id="review-cancel" class="ghost">Cancel</button>
        <button id="review-accept">Accept</button>
      </div>
    </div>
  `;

  root.querySelector<HTMLElement>('#review-cat')!.addEventListener('click', () => cb.onEditCategory(parsed));
  root.querySelector<HTMLElement>('#review-account')!.addEventListener('click', () => cb.onEditAccount(selectedAccountId));
  root.querySelector<HTMLButtonElement>('#review-accept')!.addEventListener('click', () => cb.onAccept(parsed, selectedAccountId));
  root.querySelector<HTMLButtonElement>('#review-cancel')!.addEventListener('click', () => cb.onCancel());
}

function formatCategoryLabel(
  categoryId: string,
  subcategoryId: string | null,
  categories: CategoryListItem[],
): string {
  const cat = categories.find((c) => c.id === categoryId);
  if (!cat) return categoryId;
  if (!subcategoryId) return cat.name;
  const sub = categories.find((c) => c.id === subcategoryId);
  return sub ? `${cat.name} / ${sub.name}` : cat.name;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
